import React, {useCallback, useState} from 'react';
import { useSelector } from 'react-redux';

import {formatDate, formatPosition} from '../common/formatter';
import ReportFilter from './ReportFilter';
import ReportLayoutPage from './ReportLayoutPage';
import { prefixString } from '../common/stringUtils';
import { t } from '../common/LocalizationProvider';

import {DataGrid, GridOverlay} from '@material-ui/data-grid';
import { Grid, FormControl, InputLabel, Select, MenuItem, makeStyles, Paper } from '@material-ui/core';
import { useTheme } from "@material-ui/core/styles";
import {cyberStyles} from "../cssShared/cyberTheme";
import CircularProgress from "@material-ui/core/CircularProgress";
import Tooltip from "@material-ui/core/Tooltip";

const useStyles = makeStyles((theme) => ({
  ...cyberStyles(theme),
}));

const Filter = ({ setItems, loading, setLoading }) => {
  const classes = useStyles();
  const [eventTypes, setEventTypes] = useState(['allEvents']);
  const [openSnackbar, setOpenSnackbar] = useState(null)

  const handleSubmit = useCallback( async (deviceIds, from, to, mail, headers) => {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    let query = new URLSearchParams({ from, to, mail });
    deviceIds.forEach(deviceId => query.append("deviceId", deviceId))
    if (headers.Accept !== 'application/json')
      query.append("timeZone", timeZone)

    eventTypes.forEach(it => query.append('type', it));
    const response = await fetch(`/api/reports/events?${query.toString()}`, { headers });
    if (response.ok) {
      if (mail)
        setOpenSnackbar('EmailReportAlert')
      const contentType = response.headers.get('content-type');
      if (contentType) {
        if (contentType === 'application/json') {
          const res = await response.json();
          setItems(res);
          if (res.length === 0)
            setOpenSnackbar('replayNoData')
        } else {
          window.location.assign(window.URL.createObjectURL(await response.blob()));
          setOpenSnackbar('ExcelFileExported')
        }
      }
    } else
      setOpenSnackbar('UnknownProblem')
    setLoading(null)

  },[setItems, eventTypes]);

  return (
      <ReportFilter handleSubmit={handleSubmit} openSnackbar={openSnackbar} setOpenSnackbar={setOpenSnackbar} loading={loading} setLoading={setLoading}>
        <Grid item xs={12} sm={6}>
          <FormControl variant="filled" fullWidth className={classes.formControl}>
            <InputLabel>{t('reportEventTypes')}</InputLabel>
            <Select
                value={eventTypes}
                onChange={e => setEventTypes(e.target.value)}
                multiple
                MenuProps={{PaperProps: { className: classes.periodMenuPaper },}}
            >
              <MenuItem className={classes.menuItem} value="allEvents">{t('eventAll')}</MenuItem>
              <MenuItem className={classes.menuItem} value="deviceOnline">{t('eventDeviceOnline')}</MenuItem>
              <MenuItem className={classes.menuItem} value="deviceUnknown">{t('eventDeviceUnknown')}</MenuItem>
              <MenuItem className={classes.menuItem} value="deviceOffline">{t('eventDeviceOffline')}</MenuItem>
              <MenuItem className={classes.menuItem} value="deviceSlowing">{t('eventDeviceSlowing')}</MenuItem>
              <MenuItem className={classes.menuItem} value="deviceInactive">{t('eventDeviceInactive')}</MenuItem>
              <MenuItem className={classes.menuItem} value="deviceMoving">{t('eventDeviceMoving')}</MenuItem>
              <MenuItem className={classes.menuItem} value="deviceStopped">{t('eventDeviceStopped')}</MenuItem>
              <MenuItem className={classes.menuItem} value="deviceOverspeed">{t('eventDeviceOverspeed')}</MenuItem>
              <MenuItem className={classes.menuItem} value="deviceFuelDrop">{t('eventDeviceFuelDrop')}</MenuItem>
              <MenuItem className={classes.menuItem} value="commandResult">{t('eventCommandResult')}</MenuItem>
              <MenuItem className={classes.menuItem} value="geofenceEnter">{t('eventGeofenceEnter')}</MenuItem>
              <MenuItem className={classes.menuItem} value="geofenceExit">{t('eventGeofenceExit')}</MenuItem>
              <MenuItem className={classes.menuItem} value="alarm">{t('eventAlarm')}</MenuItem>
              <MenuItem className={classes.menuItem} value="ignitionOn">{t('eventIgnitionOn')}</MenuItem>
              <MenuItem className={classes.menuItem} value="ignitionOff">{t('eventIgnitionOff')}</MenuItem>
              <MenuItem className={classes.menuItem} value="maintenance">{t('eventMaintenance')}</MenuItem>
              <MenuItem className={classes.menuItem} value="textMessage">{t('eventTextMessage')}</MenuItem>
              <MenuItem className={classes.menuItem} value="driverChanged">{t('eventDriverChanged')}</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </ReportFilter>
  );
}

const EventReportPage = () => {
  const classes = useStyles();
  const theme = useTheme();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(null);
  const geofences = useSelector(state => state.geofences.items);
  const maintenances = useSelector(state => state.maintenances.items);

  const formatGeofence = value => {
    if (value > 0) {
      const geofence = geofences[value];
      return geofence ? geofence.name : '';
    }
    return null;
  }

  const formatMaintenances = value => {
    if (value > 0) {
      const maintenance = maintenances[value];
      return maintenance ? maintenance.name : '';
    }
    return null;
  }

  const columns = [
    {
      headerName: t('reportDeviceName'),
      field: 'deviceName',
      type: 'string',
      width: 220,
      // valueFormatter: ({ value }) => formatPosition(value),
      renderCell: (params) => {
        return (
            <Tooltip title={formatPosition(params.value)} placement="top-start" arrow>
              <div>
                {formatPosition(params.value)}
              </div>
            </Tooltip>
        );
      },
    }, {
      headerName: t('positionFixTime'),
      field: 'eventTime',
      type: 'dateTime',
      width: theme.dimensions.columnWidthDate,
      valueFormatter: ({ value }) => formatDate(value),
    }, {
      headerName: t('sharedType'),
      field: 'type',
      type: 'string',
      width: theme.dimensions.columnWidthString,
      valueFormatter: ({ value }) => t(prefixString('event', value)),
    }, {
      headerName: t('sharedGeofence'),
      field: 'geofenceId',
      width: theme.dimensions.columnWidthString,
      valueFormatter: ({ value }) => formatGeofence(value),
    },{
      headerName: t('sharedMaintenance'),
      field: 'maintenanceId',
      width: theme.dimensions.columnWidthString,
      valueFormatter: ({ value }) => formatMaintenances(value),
    },];

  return (
      <ReportLayoutPage filter={<Filter setItems={setItems} loading={loading} setLoading={setLoading} />}>
        <Paper className={classes.paper}>
          <DataGrid
              loading={loading}
              rows={items}
              columns={columns}
              // hideFooterPagination
              autoHeight
              components={{
                LoadingOverlay: () => (
                    <GridOverlay>
                      <CircularProgress className={classes.color} />
                    </GridOverlay>
                )
              }}
          />
        </Paper>
      </ReportLayoutPage>
  );
}

export default EventReportPage;